import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import {
  Calendar, Loader2, ArrowRight, MoreVertical, Clock, XCircle, Repeat,
  CheckCircle2, AlertTriangle, Settings, RefreshCw, PackagePlus,
  Star, Heart, ThumbsUp, Lock, MapPin, Home,
} from "lucide-react";
import {
  CancelBookingDialog, RescheduleBookingDialog, SubscriptionDialog,
} from "../components/BookingActionDialogs";
import LongGrassApprovalCard from "../components/LongGrassApprovalCard";
import ManageServiceMenu from "../components/ManageServiceMenu";
import RecurringScheduleCard from "../components/RecurringScheduleCard";
import ManageSchedulePanel from "../components/ManageSchedulePanel";
import RecurringQuickActions from "../components/RecurringQuickActions";
import PropertiesSection from "../components/PropertiesSection";
import ReviewTipCTA from "../components/ReviewTipCTA";
import NotificationCenter from "../components/NotificationCenter";
import PushOptInBanner from "../components/PushOptInBanner";
import AddExtraWorkDialog from "../components/AddExtraWorkDialog";
import WelcomeBanner from "../components/WelcomeBanner";
import PaymentMethodChip from "../components/PaymentMethodChip";
import PaymentDueSection from "../components/PaymentDueSection";
import SavedCardPanel from "../components/SavedCardPanel";
import CustomerJobsPanel from "../components/CustomerJobsPanel";

const STATUS_PILL = {
  pending: { bg: "bg-amber-50 text-amber-800 border-amber-200", label: "Pending" },
  confirmed: { bg: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Confirmed" },
  completed: { bg: "bg-blue-50 text-blue-800 border-blue-200", label: "Completed" },
  canceled: { bg: "bg-gray-100 text-gray-600 border-gray-200", label: "Canceled" },
  cancelled: { bg: "bg-gray-100 text-gray-600 border-gray-200", label: "Canceled" },
  pending_payment_verification: { bg: "bg-amber-50 text-amber-800 border-amber-200", label: "Pending Verification" },
  // iter254 (2026-08-23) — Skipped visits keep their history row but must
  // render distinctly (never as "Pending") so the customer knows what
  // happened.  Applies to both customer-initiated and admin-initiated skips.
  skipped_by_customer: { bg: "bg-sky-50 text-sky-800 border-sky-200", label: "Skipped" },
  skipped_by_admin:    { bg: "bg-sky-50 text-sky-800 border-sky-200", label: "Skipped" },
};
const PAY_PILL = {
  paid: { bg: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Paid" },
  unpaid: { bg: "bg-gray-100 text-gray-600 border-gray-200", label: "Unpaid" },
  pay_on_arrival: { bg: "bg-amber-50 text-amber-800 border-amber-200", label: "Payment Due" },
  pending_verification: { bg: "bg-amber-50 text-amber-800 border-amber-200", label: "Awaiting Verification" },
  refund_pending: { bg: "bg-blue-50 text-blue-800 border-blue-200", label: "Refund Pending" },
  refund_review: { bg: "bg-amber-50 text-amber-800 border-amber-200", label: "Refund In Review" },
  no_refund_needed: { bg: "bg-gray-100 text-gray-600 border-gray-200", label: "No Refund" },
  payment_pending: { bg: "bg-rose-50 text-rose-800 border-rose-200", label: "Payment Due" },
  // iter254 — Skipped visits never accrue an invoice or payment reminder.
  not_applicable: { bg: "bg-gray-50 text-gray-500 border-gray-200", label: "No Charge" },
  autopay_scheduled: { bg: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "AutoPay" },
};

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [offers, setOffers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("upcoming"); // upcoming | history | quotes | offers | payments | settings
  const [cancelTarget, setCancelTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [subTarget, setSubTarget] = useState(null);
  // iter253 — bump on each Dashboard refresh so <RecurringScheduleCard>
  // re-fetches its own schedule (needed to surface a fresh
  // "Paused until …" banner after the RecurringQuickActions Pause flow).
  const [recurringReloadKey, setRecurringReloadKey] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  const refresh = () => {
    setLoadingBookings(true);
    api.get("/bookings/me")
      .then(({ data }) => setBookings(data.bookings || []))
      .catch(() => setBookings([]))
      .finally(() => setLoadingBookings(false));
    api.get("/quotes/me/list")
      .then(({ data }) => setQuotes(data.quotes || []))
      .catch(() => setQuotes([]));
    api.get("/offers/me/list")
      .then(({ data }) => setOffers(data.offers || []))
      .catch(() => setOffers([]));
    api.get("/me/payments")
      .then(({ data }) => setPayments(data.payments || []))
      .catch(() => setPayments([]));
    // iter253 — trigger RecurringScheduleCard reload
    setRecurringReloadKey((k) => k + 1);
  };

  useEffect(() => { if (user) refresh(); /* eslint-disable-line */ }, [user]);

  const { upcoming, history } = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const up = [], hi = [];
    for (const b of bookings) {
      const isCanceled = ["canceled", "cancelled"].includes(b.status);
      const isCompleted = b.status === "completed";
      // iter254 — skipped visits belong to History; the auto-created
      // next visit is what shows up in Upcoming.
      const isSkipped = ["skipped_by_customer", "skipped_by_admin"].includes(b.status);
      const past = b.preferred_date ? new Date(b.preferred_date + "T00:00:00") < today : false;
      if (isCanceled || isCompleted || isSkipped || past) hi.push(b);
      else up.push(b);
    }
    return { upcoming: up, history: hi };
  }, [bookings]);

  // iter252 (2026-08-23) — Customer-level active recurring flag.  Owner
  // directive: Skip / Pause / Change Frequency buttons must appear on
  // EVERY service card as long as the customer has ANY active recurring
  // plan — even when the specific card is a one-time booking.  The
  // /api/me/recurring/* endpoints operate at customer level, not per
  // booking, so we surface the same three quick actions everywhere.
  const { hasActiveRecurring, activeRecurringBooking } = useMemo(() => {
    const active = bookings.find((b) => {
      const freq = b.frequency;
      const isRec = freq && freq !== "one_time" && freq !== "every_visit";
      if (!isRec) return false;
      const subCancelled = ["cancelled", "canceled"].includes(b.subscription_status || "");
      const bkCancelled = ["cancelled", "canceled"].includes(b.status || "");
      return !subCancelled && !bkCancelled;
    });
    return { hasActiveRecurring: !!active, activeRecurringBooking: active || null };
  }, [bookings]);

  if (loading || !user) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#047857]" /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="dashboard-page">
      <WelcomeBanner />
      <PushOptInBanner />
      <div className="flex items-center justify-between flex-wrap gap-4 mt-4 first:mt-0">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#047857] font-bold">Dashboard</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">Welcome back, {user.name?.split(" ")[0] || "neighbor"}.</h1>
        </div>
        <div className="flex gap-2 items-center">
          <NotificationCenter />
          <Link to="/book"><Button className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="dash-book-btn">Book service</Button></Link>
          <Button onClick={logout} variant="outline" className="rounded-full" data-testid="dash-signout-btn">Sign out</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Upcoming" value={upcoming.length} testid="stat-upcoming" />
        <Stat label="History" value={history.length} testid="stat-history" />
        <Stat label="Active subs" value={bookings.filter(b => b.subscription_status === "active").length} testid="stat-subs" />
        <Stat label="Lifetime spend" value={`$${bookings.filter(b => b.payment_status === "paid").reduce((s, b) => s + (b.price || 0), 0).toFixed(0)}`} testid="stat-spend" />
      </div>

      {/* Payment Due — single consolidated section at top of dashboard
          (iter125). Replaces per-card Pay Now buttons. Renders only when
          the customer has unpaid invoices or an in-progress Stripe
          checkout ("Payment Pending"). Fetches its own data. */}
      <PaymentDueSection onRefresh={refresh} />

      {/* My recurring service */}
      <div className="mt-6">
        <RecurringScheduleCard reloadKey={recurringReloadKey} />
      </div>

      {/* iter255 (2026-08-24) — Multi-Property support (Phase 1). Each
          customer account can carry many properties; each stores its
          own address, square footage, and yard-area default so pricing
          per property never mixes with any other property. */}
      <PropertiesSection />

      {/* Post-service CTA — same design as our email/SMS review request,
          shown once the customer has at least one completed + paid visit.
          iter105: Only eligible bookings unlock reviews / tips / likes. */}
      {history.some((b) => b.status === "completed" && (b.payment_status === "paid" || b.payment_status === "pay_on_arrival") && !b.duplicate_of_booking_id && !b.archived_at) && (
        <div className="mt-4" data-testid="dashboard-review-tip-cta">
          <ReviewTipCTA
            bookingId={
              [...history]
                .filter((b) => b.status === "completed" && (b.payment_status === "paid" || b.payment_status === "pay_on_arrival") && !b.duplicate_of_booking_id && !b.archived_at)
                .sort((a, b) => String(b.completed_at || b.preferred_date || "").localeCompare(String(a.completed_at || a.preferred_date || "")))[0]?.booking_id
            }
          />
        </div>
      )}

      {/* Tabs */}
      <div className="mt-6 flex gap-1 p-1 bg-gray-100 rounded-full w-fit text-xs sm:text-sm flex-wrap">
        <TabBtn id="upcoming" active={tab === "upcoming"} onClick={() => setTab("upcoming")} testid="tab-upcoming">Upcoming</TabBtn>
        <TabBtn id="history" active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">History</TabBtn>
        <TabBtn id="quotes" active={tab === "quotes"} onClick={() => setTab("quotes")} testid="tab-quotes">
          Quotes {quotes.length > 0 && <span className="ml-1 inline-flex items-center justify-center text-[10px] font-extrabold bg-emerald-100 text-[#047857] rounded-full px-1.5 min-w-[18px] h-[18px]">{quotes.length}</span>}
        </TabBtn>
        <TabBtn id="offers" active={tab === "offers"} onClick={() => setTab("offers")} testid="tab-offers">
          Offers {offers.length > 0 && <span className="ml-1 inline-flex items-center justify-center text-[10px] font-extrabold bg-amber-100 text-amber-800 rounded-full px-1.5 min-w-[18px] h-[18px]">{offers.length}</span>}
        </TabBtn>
        <TabBtn id="payments" active={tab === "payments"} onClick={() => setTab("payments")} testid="tab-payments">
          Payments {payments.filter((p) => ["paid","complete"].includes(p.payment_status)).length > 0 && <span className="ml-1 inline-flex items-center justify-center text-[10px] font-extrabold bg-emerald-100 text-[#047857] rounded-full px-1.5 min-w-[18px] h-[18px]">{payments.filter((p) => ["paid","complete"].includes(p.payment_status)).length}</span>}
        </TabBtn>
        <TabBtn id="settings" active={tab === "settings"} onClick={() => setTab("settings")} testid="tab-settings">Settings</TabBtn>
      </div>

      <div className="mt-5">
        {tab === "upcoming" && (
          <>
            {/* iter104 — Manage Schedule panel: change frequency, skip
                next, cancel all future, view audit history */}
            <ManageSchedulePanel user={user} onRefresh={refresh} />
            <BookingList
              list={upcoming} loading={loadingBookings} emptyLabel="No upcoming bookings."
              onCancel={setCancelTarget} onReschedule={setRescheduleTarget} onManageSub={setSubTarget}
              interactive onRefresh={refresh}
              hasActiveRecurring={hasActiveRecurring}
              activeRecurringBooking={activeRecurringBooking}
            />
          </>
        )}
        {tab === "history" && (
          <BookingList list={history} loading={loadingBookings} emptyLabel="No past bookings yet." />
        )}
        {tab === "quotes" && <QuotesList quotes={quotes} />}
        {tab === "offers" && <OffersList offers={offers} />}
        {tab === "payments" && <PaymentsList payments={payments} navigate={navigate} />}
        {tab === "settings" && <SettingsPanel user={user} />}
      </div>

      <CancelBookingDialog booking={cancelTarget} open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)} onDone={refresh} />
      <RescheduleBookingDialog booking={rescheduleTarget} open={!!rescheduleTarget} onOpenChange={(v) => !v && setRescheduleTarget(null)} onDone={refresh} />
      <SubscriptionDialog booking={subTarget} open={!!subTarget} onOpenChange={(v) => !v && setSubTarget(null)} onDone={refresh} />
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#047857] font-extrabold">{label}</div>
      <div className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight font-[Outfit]">{value}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-4 rounded-full font-bold transition ${active ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function BookingList({ list, loading, emptyLabel, onCancel, onReschedule, onManageSub, interactive, onRefresh, hasActiveRecurring, activeRecurringBooking }) {
  if (loading) return <div className="py-6"><Loader2 className="w-6 h-6 animate-spin text-[#047857]" /></div>;
  if (!list || list.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
        <Calendar className="w-10 h-10 text-gray-400 mx-auto" />
        <p className="mt-3 text-gray-600">{emptyLabel}</p>
        <Link to="/book"><Button className="mt-4 rounded-full bg-[#047857] hover:bg-[#065F46] text-white">Book a service</Button></Link>
      </div>
    );
  }
  const renderCard = (b) => (
    <BookingCard key={b.booking_id} b={b}
      onCancel={onCancel} onReschedule={onReschedule} onManageSub={onManageSub}
      interactive={interactive} onRefresh={onRefresh}
      hasActiveRecurring={hasActiveRecurring}
      activeRecurringBooking={activeRecurringBooking} />
  );
  // iter260 — multi-property customers see each property as its own section.
  const groups = [];
  const byKey = {};
  for (const b of list) {
    const key = b.property_id || "_none";
    if (!byKey[key]) {
      byKey[key] = { key, label: b.property_label || (b.property_id ? "Property" : "Other bookings"),
                     address: b.formatted_address || b.address || "", items: [] };
      groups.push(byKey[key]);
    }
    byKey[key].items.push(b);
  }
  if (groups.length < 2) return <div className="grid gap-3">{list.map(renderCard)}</div>;
  return (
    <div className="grid gap-6" data-testid="bookings-by-property">
      {groups.map((g) => (
        <section key={g.key} data-testid={`property-group-${g.key}`}>
          <div className="flex items-baseline gap-2 mb-2 border-b border-gray-200 pb-1.5">
            <Home className="w-4 h-4 text-[#047857] self-center" />
            <h4 className="text-[15px] font-extrabold tracking-tight text-gray-900 font-[Outfit]">{g.label}</h4>
            <span className="text-[11px] text-gray-500 truncate">{g.address}</span>
            <span className="ml-auto text-[10px] font-extrabold uppercase tracking-wider text-[#047857]">
              {g.items.length} {g.items.length === 1 ? "visit" : "visits"}
            </span>
          </div>
          <div className="grid gap-3">{g.items.map(renderCard)}</div>
        </section>
      ))}
    </div>
  );
}

function BookingCard({ b, onCancel, onReschedule, onManageSub, interactive, onRefresh, hasActiveRecurring, activeRecurringBooking }) {
  const [menu, setMenu] = useState(false);
  const [extraWork, setExtraWork] = useState(false);
  const sp = STATUS_PILL[b.status] || STATUS_PILL.pending;
  const pp = PAY_PILL[b.payment_status] || PAY_PILL.unpaid;
  const isRecurring = b.frequency && b.frequency !== "one_time" && b.frequency !== "every_visit";
  const subStatus = b.subscription_status;
  const canCancel = !["canceled", "cancelled", "completed",
                       "skipped_by_customer", "skipped_by_admin"].includes(b.status);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 relative" data-testid={`booking-${b.booking_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full border ${sp.bg}`}>{sp.label}</span>
            <span className={`px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full border ${pp.bg}`}>{pp.label}</span>
            {isRecurring && subStatus && (
              <span className={`px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full border ${subStatus === "paused" ? "bg-amber-50 text-amber-800 border-amber-200" : subStatus === "cancelled" ? "bg-gray-100 text-gray-600 border-gray-200" : "bg-emerald-50 text-[#047857] border-emerald-200"}`}>
                Sub · {subStatus}
              </span>
            )}
          </div>
          <div className="mt-2 text-lg font-extrabold text-gray-900 leading-tight">{b.service_name}</div>
          {b.property_label && (
            <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-extrabold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                 data-testid={`dashboard-property-${b.booking_id}`}>
              <Home className="w-3 h-3" /> {b.property_label}
            </div>
          )}
          {Array.isArray(b.additional_services) && b.additional_services.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1" data-testid={`dashboard-addons-${b.booking_id}`}>
              {b.additional_services.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.1em] rounded-full bg-emerald-50 text-[#047857] border border-emerald-200">
                  + {a.service_name}{a.price ? ` · $${a.price.toFixed(0)}` : ""}
                </span>
              ))}
            </div>
          )}
          <div className="mt-0.5 text-sm text-gray-600 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {b.preferred_date} · {b.preferred_time}</div>
          <div className="mt-0.5 text-xs text-gray-500 truncate">{b.formatted_address || b.address}</div>
          {/* iter126 — Show which property areas the customer selected. */}
          {b.areas_label && (
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                 data-testid={`dashboard-areas-${b.booking_id}`}>
              <MapPin className="w-3 h-3" /> {b.areas_label}
            </div>
          )}
          {b.frequency_label && (
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-[#047857]">
              <RefreshCw className="w-3 h-3" /> {b.frequency_label}
            </div>
          )}
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            <PaymentMethodChip method={b.payment_method} methodLabel={b.payment_method_label} />
            {b.payment_status === "paid" && b.stripe_payment_intent_id && (
              <span className="text-[10px] text-gray-500 font-mono" data-testid={`dashboard-txn-${b.booking_id}`}>
                Txn: {String(b.stripe_payment_intent_id).slice(-12)}
              </span>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-2xl font-extrabold text-[#047857] tabular-nums">${b.price?.toFixed(2)}</div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-gray-500 font-bold mt-0.5">#{b.booking_id.slice(-6)}</div>
        </div>
      </div>

      {interactive && canCancel && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => onReschedule(b)} className="rounded-full h-9 text-xs" data-testid={`reschedule-${b.booking_id}`}>
            <Calendar className="w-3.5 h-3.5 mr-1" /> Reschedule
          </Button>
          <Button size="sm" variant="outline" onClick={() => setExtraWork(true)} className="rounded-full h-9 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid={`add-extra-work-${b.booking_id}`}>
            <PackagePlus className="w-3.5 h-3.5 mr-1" /> Add Extra Work
          </Button>
          <ManageServiceMenu booking={b} onUpdated={onRefresh} variant="row" />
          {isRecurring && (
            <Button size="sm" variant="outline" onClick={() => onManageSub(b)} className="rounded-full h-9 text-xs" data-testid={`manage-sub-${b.booking_id}`}>
              <Repeat className="w-3.5 h-3.5 mr-1" /> Manage subscription
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onCancel(b)} className="rounded-full h-9 text-xs border-red-200 text-red-700 hover:bg-red-50" data-testid={`cancel-${b.booking_id}`}>
            <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <AddExtraWorkDialog open={extraWork} onOpenChange={setExtraWork} booking={b} mode="customer" onSaved={onRefresh} />
        </div>
      )}

      {/* iter252 (2026-08-23) — Quick recurring actions surfaced on every
          service card whenever the CUSTOMER has an active recurring
          subscription (not just this specific card).  Owner directive:
          Skip / Pause / Change Frequency must remain visible even when
          this particular card is a one-time booking, as long as the
          customer has at least one active recurring plan. */}
      {interactive && canCancel && hasActiveRecurring && (
        <RecurringQuickActions
          booking={activeRecurringBooking || b}
          cardBookingId={b.booking_id}
          cardPropertyId={b.property_id}
          onRefresh={onRefresh}
        />
      )}

      {/* Payment CTA removed here — a single "Payment Due" section at the
          top of the dashboard handles all unpaid invoices (iter125). */}

      {/* iter135 — Per-service Jobs panel (one row per service in the visit) */}
      <CustomerJobsPanel bookingId={b.booking_id} autopayEnabled={b.customer_autopay_enabled} onRefresh={onRefresh} />

      {/* iter105 — Per-visit feedback quick actions. Locked (hidden) unless
          this specific booking is completed + paid + not cancelled/archived/dup. */}
      <BookingFeedbackRow booking={b} onDone={onRefresh} />

      {/* Long-grass surcharge (action required for customer) */}
      <LongGrassApprovalCard booking={b} onUpdated={onRefresh} />

      {b.refund_status && b.refund_status !== "no_refund_needed" && (
        <div className={`mt-3 rounded-lg p-2.5 text-xs flex items-start gap-2 ${b.refund_status === "refund_pending" ? "bg-blue-50 text-blue-800" : "bg-amber-50 text-amber-800"}`}>
          {b.refund_status === "refund_pending" ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
          <span>
            {b.refund_status === "refund_pending"
              ? "Refund is being processed via your original payment method."
              : "Refund is being reviewed by our team. We'll contact you shortly."}
          </span>
        </div>
      )}
    </div>
  );
}

// iter105 — Per-booking feedback quick actions. Renders NOTHING unless the
// booking is completed + paid AND not cancelled/duplicate/archived. Each
// completed visit unlocks its own review/like/tip row (important for
// recurring services — every visit is gated independently).
function BookingFeedbackRow({ booking, onDone }) {
  const [reviewUrl, setReviewUrl] = useState("");
  const [likeStatus, setLikeStatus] = useState(booking.like_status || "not_sent");
  const [tipStatus, setTipStatus] = useState(booking.tip_status || "not_sent");
  const [busy, setBusy] = useState(false);

  const eligible =
    booking.status === "completed" &&
    (booking.payment_status === "paid" || booking.payment_status === "pay_on_arrival") &&
    !booking.duplicate_of_booking_id &&
    !booking.archived_at;

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/public");
        if (!cancelled) setReviewUrl(data?.google_review_url || "");
      } catch { /* silent */ }
      try {
        const { data } = await api.get(`/bookings/${booking.booking_id}/feedback`);
        if (cancelled) return;
        setLikeStatus(data?.like_status || "not_sent");
        setTipStatus(data?.tip_status || "not_sent");
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [booking.booking_id, eligible]);

  if (!eligible) return null;

  const liked = likeStatus === "submitted";
  const tipped = tipStatus === "submitted";

  const doLike = async () => {
    if (liked) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/like`);
      setLikeStatus("submitted");
      (await import("sonner")).toast.success("Thanks for the love! 💚");
      onDone?.();
    } catch (e) {
      (await import("sonner")).toast.error(e?.response?.data?.detail || "Could not save your like");
    } finally {
      setBusy(false);
    }
  };

  const clickReview = async () => {
    try { await api.post(`/reviews/click/${booking.booking_id}`); } catch { /* silent */ }
  };

  return (
    <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3" data-testid={`feedback-row-${booking.booking_id}`}>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#047857] font-extrabold mb-2 flex items-center gap-1.5">
        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> Loved this visit?
      </div>
      <div className="flex flex-wrap gap-2">
        <a
          href={reviewUrl || "https://g.page/r/"}
          target="_blank"
          rel="noopener noreferrer"
          onClick={clickReview}
          data-testid={`feedback-review-${booking.booking_id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#047857] hover:bg-[#065F46] text-white text-[12px] font-extrabold transition-colors"
        >
          <Star className="w-3.5 h-3.5" /> Google Review
        </a>
        <button
          type="button"
          onClick={doLike}
          disabled={liked || busy}
          data-testid={`feedback-like-${booking.booking_id}`}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-extrabold transition-colors ${liked ? "bg-rose-100 text-rose-800 cursor-default" : "bg-rose-500 hover:bg-rose-600 text-white"}`}
        >
          <ThumbsUp className="w-3.5 h-3.5" /> {liked ? "Loved it" : "Loved it?"}
        </button>
        {tipped ? (
          <span
            data-testid={`feedback-tip-sent-${booking.booking_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-[12px] font-extrabold"
          >
            <Heart className="w-3.5 h-3.5 fill-emerald-500 text-emerald-500" /> Tip sent
          </span>
        ) : (
          <Link
            to={`/tip/${booking.booking_id}`}
            data-testid={`feedback-tip-${booking.booking_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-extrabold transition-colors"
          >
            <Heart className="w-3.5 h-3.5" /> Leave a Tip
          </Link>
        )}
      </div>
    </div>
  );
}



// ============== Settings Panel ==============
// iter76: Customer-selectable preferred payment methods are limited to
// Stripe. Offline methods remain in admin CRM for manual jobs / historical
// records but are hidden from the customer profile picker.
const PAYMENT_METHOD_OPTIONS = [
  { id: "", label: "No preference (ask each time)" },
  { id: "stripe", label: "Credit / Debit Card" },
];

function SettingsPanel({ user }) {
  const { setUser } = useAuth();
  const [form, setForm] = useState({
    name: user.name || "",
    phone: user.phone || "",
    default_address: user.default_address || "",
    default_area: user.default_area || "",
    preferred_payment_method: user.preferred_payment_method || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/auth/me/profile", patch);
      setUser(data);
      // eslint-disable-next-line
      (await import("sonner")).toast.success("Saved");
    } catch (e) {
      // eslint-disable-next-line
      (await import("sonner")).toast.error("Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="settings-panel">
      <SavedCardPanel />
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#047857] font-extrabold">Contact information</div>
        <div className="mt-3 space-y-3">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="settings-name" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="settings-phone" />
          <Field label="Email" value={user.email} disabled />
        </div>
        <Button onClick={() => save({ name: form.name, phone: form.phone })} disabled={busy} className="mt-4 rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="settings-save-contact">
          {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Save contact
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#047857] font-extrabold">Service address</div>
        <div className="mt-3 space-y-3">
          <Field label="Default address" value={form.default_address} onChange={(v) => setForm({ ...form, default_address: v })} testid="settings-address" />
          <Field label="Neighborhood" value={form.default_area} onChange={(v) => setForm({ ...form, default_area: v })} testid="settings-area" />
        </div>
        <Button onClick={() => save({ default_address: form.default_address, default_area: form.default_area })} disabled={busy} className="mt-4 rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="settings-save-address">
          {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Save address
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 lg:col-span-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#047857] font-extrabold">Preferred payment method</div>
        <p className="mt-1 text-xs text-gray-600">We'll preselect this on your next booking. You can still change at checkout.</p>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {PAYMENT_METHOD_OPTIONS.map((p) => {
            const active = form.preferred_payment_method === p.id;
            return (
              <button
                key={p.id || "none"}
                type="button"
                onClick={() => {
                  setForm({ ...form, preferred_payment_method: p.id });
                  save({ preferred_payment_method: p.id });
                }}
                className={`text-left p-3 rounded-xl border transition ${active ? "bg-[#062818] border-[#062818] text-white" : "bg-white border-gray-200 hover:border-[#10B981]"}`}
                data-testid={`settings-pay-${p.id || "none"}`}
              >
                <div className="font-bold text-sm">{p.label}</div>
                {active && <div className="mt-1 text-[10px] uppercase tracking-[0.14em] font-extrabold text-emerald-300">Selected</div>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.16em] text-gray-500 font-bold mb-1">{label}</label>
      <input
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.value)}
        className="w-full h-11 px-3 rounded-xl border border-gray-200 disabled:bg-gray-50 disabled:text-gray-500"
        data-testid={testid}
      />
    </div>
  );
}


function QuotesList({ quotes }) {
  if (!quotes || quotes.length === 0) {
    return <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-500">No quotes yet. JDSN will send you one if they recommend additional services.</div>;
  }
  const STATUS_STYLES = {
    sent: "bg-sky-100 text-sky-800",
    viewed: "bg-sky-100 text-sky-800",
    accepted: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
    changes_requested: "bg-amber-100 text-amber-800",
    expired: "bg-gray-200 text-gray-600",
    draft: "bg-gray-100 text-gray-600",
  };
  return (
    <div className="space-y-3" data-testid="quotes-list">
      {quotes.map((q) => (
        <Link key={q.quote_id} to={`/quote/${q.quote_id}`} className="block bg-white rounded-2xl border border-gray-200 p-5 hover:border-emerald-300 transition-all" data-testid={`my-quote-${q.quote_id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-bold text-gray-900">{q.title}</div>
              <div className="text-xs text-gray-500 mt-1">
                {(q.items || []).length} item{q.items?.length === 1 ? "" : "s"} · #{q.quote_id?.slice(-6)} · Expires {q.expires_at?.split("T")[0]}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-extrabold text-[#047857] tabular-nums">${q.total?.toFixed(2)}</div>
              <span className={`text-[10px] uppercase tracking-[0.14em] font-extrabold px-2 py-0.5 rounded-full ${STATUS_STYLES[q.status] || "bg-gray-100 text-gray-700"}`}>
                {(q.status || "draft").replace(/_/g, " ")}
              </span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function OffersList({ offers }) {
  if (!offers || offers.length === 0) {
    return <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-500">No offers yet. Use the estimate page to propose a price.</div>;
  }
  const STATUS_STYLES = {
    pending: "bg-amber-100 text-amber-800",
    counter_offered: "bg-sky-100 text-sky-800",
    accepted: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
    expired: "bg-gray-200 text-gray-600",
  };
  return (
    <div className="space-y-3" data-testid="offers-list">
      {offers.map((o) => {
        const ourTurn = !["accepted","rejected","expired"].includes(o.status) && o.last_action_by === "admin";
        return (
          <Link key={o.offer_id} to={`/offer/${o.offer_id}`} className="block bg-white rounded-2xl border border-gray-200 p-5 hover:border-emerald-300 transition-all" data-testid={`my-offer-${o.offer_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="font-bold text-gray-900">{o.service_name || o.service_key || "Service"}</div>
                <div className="text-xs text-gray-500 mt-1">
                  #{o.offer_id?.slice(-6)} · Round {o.counter_count || 0}/5{o.preferred_date && ` · For ${o.preferred_date}`}
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-extrabold text-[#047857] tabular-nums">${(o.offered_price || 0).toFixed(2)}</div>
                <div className="text-[11px] text-gray-500">est. ${(o.estimated_price || 0).toFixed(2)}</div>
                <span className={`mt-1 inline-block text-[10px] uppercase tracking-[0.14em] font-extrabold px-2 py-0.5 rounded-full ${STATUS_STYLES[o.status] || "bg-gray-100 text-gray-700"}`}>
                  {(o.status || "pending").replace(/_/g, " ")}{ourTurn && " · your turn"}
                </span>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function PaymentsList({ payments, navigate }) {
  if (!payments || payments.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center" data-testid="payments-empty">
        <Calendar className="w-10 h-10 text-gray-400 mx-auto" />
        <p className="mt-3 text-gray-600">No payments yet.</p>
        <Link to="/book"><Button className="mt-4 rounded-full bg-[#047857] hover:bg-[#065F46] text-white">Book a service</Button></Link>
      </div>
    );
  }
  const PAY_BADGE = {
    paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
    complete: "bg-emerald-50 text-emerald-800 border-emerald-200",
    initiated: "bg-amber-50 text-amber-800 border-amber-200",
    refunded: "bg-blue-50 text-blue-800 border-blue-200",
    failed: "bg-rose-50 text-rose-800 border-rose-200",
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden divide-y divide-gray-100" data-testid="payments-list">
      {payments.map((p) => {
        const paid = p.payment_status === "paid" || p.payment_status === "complete";
        const when = p.paid_at || p.created_at;
        return (
          <button
            key={p.tx_id || p.session_id}
            type="button"
            onClick={() => paid && p.tx_id && navigate(`/receipt/${p.tx_id}`)}
            data-testid={`payment-row-${p.tx_id || p.session_id}`}
            className="w-full text-left px-4 sm:px-5 py-4 hover:bg-gray-50 flex items-start gap-3 disabled:opacity-60"
            disabled={!paid || !p.tx_id}
          >
            <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${paid ? "bg-emerald-50 text-[#047857]" : "bg-amber-50 text-amber-700"}`}>
              {paid ? <CheckCircle2 className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-extrabold tracking-tight text-gray-900 truncate">
                {p.kind === "tip" ? "Tip" : p.payment_type === "deposit" ? "50% Deposit" : p.payment_type === "full" ? "Paid in full" : "Payment"}
                {p.booking_id && <span className="ml-1 text-gray-500 font-normal">· #{p.booking_id.slice(-6)}</span>}
                {p.quote_id && <span className="ml-1 text-gray-500 font-normal">· quote {p.quote_id.slice(-6)}</span>}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full border ${PAY_BADGE[p.payment_status] || "bg-gray-50 text-gray-700 border-gray-200"}`}>
                  {p.payment_status}
                </span>
                <span className="text-[12px] text-gray-500">{when ? new Date(when).toLocaleString() : "—"}</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-lg font-extrabold tabular-nums text-gray-900">${(p.amount || 0).toFixed(2)}</div>
              {paid && p.tx_id && (
                <div className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-[0.14em] font-extrabold text-[#047857]">
                  Receipt <ArrowRight className="w-3 h-3" />
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
